import { IOpporunitiesContext } from 'pages/Opportunities/context/OpportunitiesContext.types';
import { UseInfiniteQueryOptions, QueryKey, useInfiniteQuery, useQuery, useMutation } from 'react-query';
import { OPPORTUNITIES_QUERY_STRING } from 'shared/constants/QUERY_STRING';
import opportunitiesDao from 'shared/dao/opportunitieDao';
import { IProjectAttributes } from 'shared/interfaces/IProject';
import { IRoleAttributes } from 'shared/interfaces/IRole';

import { IAxiosErrorResponse } from 'shared/interfaces/utils/IAxios';
import {
  IApplyToOpportunityPayload,
  IOpportunities,
  IOpportunitiesPublicAggregates,
  IOpportunitiesRemoveProjectResponse,
  IOpportunitiesTabAggregates,
  IProjectRoles,
  ISpecificProjectResponse,
  ISpecificRoleResponse,
} from 'shared/interfaces/utils/IOpportunities';
import { authToken } from 'shared/utils/authToken';

const {
  getOpportunities: getOpportunitiesDao,
  getProjectInfo: getProjectInfoDao,
  getRoleInfo: getRoleInfoDao,
  getTabAggregates: getTabAggregatesDao,
  removeProjects: removeProjectsDao,
  applyToRole: applyToRoleDao,
  getPublicAggregates: getPublicAggregatesDao,
  getProjectRoles: getProjectRolesDao,
  checkAudition: checkAuditionDao,
} = opportunitiesDao();
const { getAuthToken } = authToken();

const subdomain = window.location.hostname.split('.')[0];
const isJob = subdomain === 'jobs';

export const opportunitiesServices = () => {
  const getOpportunities = (
    payload?: any,
    options?: Omit<
      UseInfiniteQueryOptions<IOpportunities, IAxiosErrorResponse, IOpportunities, QueryKey>,
      'queryKey' | 'queryFn'
    >,
  ) => {
    return useInfiniteQuery<IOpportunities, IAxiosErrorResponse, IOpportunities>(
      [OPPORTUNITIES_QUERY_STRING.OPPORTUNITY_LIST, payload],
      ({ pageParam = '' }) => getOpportunitiesDao({ ...payload, after: pageParam }),
      {
        getNextPageParam: (lastPage) => {
          if (!lastPage.data) return undefined;
          return lastPage.data.opportunities_connection.pageInfo.hasNextPage
            ? lastPage.data.opportunities_connection.pageInfo.endCursor
            : undefined;
        },
        enabled: options?.enabled,
      },
    );
  };

  const getProjectRoles = (
    payload: any,
    complete: boolean,
    options?: Omit<
      UseInfiniteQueryOptions<IProjectRoles, IAxiosErrorResponse, IProjectRoles, QueryKey>,
      'queryKey' | 'queryFn'
    >,
  ) => {
    return useInfiniteQuery<IProjectRoles, IAxiosErrorResponse, IProjectRoles>(
      [OPPORTUNITIES_QUERY_STRING.OPPORTUNITY_LIST, payload],
      ({ pageParam = '' }) => getProjectRolesDao({ ...payload, after: pageParam }, complete),
      {
        getNextPageParam: (lastPage) => {
          if (!lastPage.data) return undefined;
          return lastPage.data.casting_role_opportunities_connection.pageInfo.hasNextPage
            ? lastPage.data.casting_role_opportunities_connection.pageInfo.endCursor
            : undefined;
        },
        enabled: options?.enabled,
      },
    );
  };

  const getProjectInfo = (id: string, isPublic?: boolean, withRoles?: boolean) => {
    return useQuery<ISpecificProjectResponse, IAxiosErrorResponse>(
      [OPPORTUNITIES_QUERY_STRING.SPECIFIC_PROJECT, id],
      () => getProjectInfoDao(id, isPublic, withRoles),
      {
        enabled: !!id,
      },
    );
  };

  const getRoleInfo = (id: string, isPublic?: boolean) => {
    return useQuery<ISpecificRoleResponse, IAxiosErrorResponse>(
      [OPPORTUNITIES_QUERY_STRING.SPECIFIC_ROLE, id],
      () => getRoleInfoDao(id, isPublic),
      {
        enabled: !!id,
      },
    );
  };

  const getTabAggregates = (payload: IOpporunitiesContext['filters']) => {
    return useQuery<IOpportunitiesTabAggregates, IAxiosErrorResponse>(
      [OPPORTUNITIES_QUERY_STRING.OPPORTUNITIES_TAB_AGGREGATES, payload],
      () => getTabAggregatesDao(payload),
      {
        enabled: !isJob,
      },
    );
  };

  const getPublicAggregates = (payload: Partial<IOpporunitiesContext['filters']>) => {
    return useQuery<IOpportunitiesPublicAggregates, IAxiosErrorResponse>(
      [OPPORTUNITIES_QUERY_STRING.OPPORTUNITIES_PUBLIC_AGGREGATES, payload],
      () => getPublicAggregatesDao(payload),
      {
        enabled: isJob,
      },
    );
  };

  const removeProjects = () => {
    return useMutation<IOpportunitiesRemoveProjectResponse, IAxiosErrorResponse, { ids: string[] }>(({ ids }) =>
      removeProjectsDao(ids),
    );
  };

  const applyToRole = () => {
    return useMutation<{ message: string }, IAxiosErrorResponse, IApplyToOpportunityPayload>(
      (payload: IApplyToOpportunityPayload) => applyToRoleDao(payload),
    );
  };

  const fetchProjectRolesData = (projectId: string, roleIds: string[]) => {
    return useQuery<IProjectRoles, IAxiosErrorResponse>(
      [OPPORTUNITIES_QUERY_STRING.PROJECT_ROLES, projectId],
      () =>
        getProjectRolesDao(
          {
            project_ids: [projectId],
            except_role_ids: roleIds,
          },
          true,
          true,
        ),
      {
        enabled: !!roleIds.length,
      },
    );
  };

  const checkAutidion = (casting_role_id: string) => {
    return useQuery<{ data: boolean }, IAxiosErrorResponse>(
      [OPPORTUNITIES_QUERY_STRING.OPPORTUNITIES_AUDITION_CHECKER, casting_role_id],
      () => checkAuditionDao(casting_role_id),
      {
        enabled: !!casting_role_id,
      },
    );
  };

  return {
    checkAutidion,
    fetchProjectRolesData,
    getProjectRoles,
    getPublicAggregates,
    applyToRole,
    removeProjects,
    getTabAggregates,
    getRoleInfo,
    getProjectInfo,
    getOpportunities,
  };
};
